// src/contexts/languageContext.js
import React, { createContext, useContext, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './authContext';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const { user } = useAuth();

  // Get current language from user preferences or fallback to 'en'
  const getCurrentLanguage = useCallback(() => {
    return user?.metaData?.preferences?.language || 'en';
  }, [user?.metaData?.preferences?.language]);

  // Check if current language is RTL (Arabic)
  const isRTL = getCurrentLanguage() === 'ar';

  // Change language function
  const changeLanguage = useCallback((languageCode) => {
    i18n.changeLanguage(languageCode);
    
    // Update document direction and lang attribute
    document.documentElement.dir = languageCode === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = languageCode;
    
    // Update body class for styling
    if (languageCode === 'ar') {
      document.body.classList.add('rtl');
      document.body.classList.remove('ltr');
    } else {
      document.body.classList.add('ltr');
      document.body.classList.remove('rtl');
    }
  }, [i18n]);

  // Initialize language on mount and when user changes
  useEffect(() => {
    const userLanguage = getCurrentLanguage();
    changeLanguage(userLanguage);
  }, [user?.metaData?.preferences?.language, getCurrentLanguage, changeLanguage]);

  // Get language direction class for components
  const getDirectionClass = (baseClass = '') => {
    return isRTL ? `${baseClass} rtl` : `${baseClass} ltr`;
  };

  // Get text alignment based on language direction
  const getTextAlign = () => {
    return isRTL ? 'text-right' : 'text-left';
  };

  // Get flex direction for components
  const getFlexDirection = (reverse = false) => {
    if (isRTL) {
      return reverse ? 'flex-row' : 'flex-row-reverse';
    } else {
      return reverse ? 'flex-row-reverse' : 'flex-row';
    }
  };

  // Get margin/padding classes with RTL support
  const getSpacingClass = (type, direction, size) => {
    // type: 'm' for margin, 'p' for padding
    // direction: 'l' for left, 'r' for right, 't' for top, 'b' for bottom
    // size: the spacing size number
    
    if (direction === 'l' && isRTL) {
      return `${type}r-${size}`;
    } else if (direction === 'r' && isRTL) {
      return `${type}l-${size}`;
    } else {
      return `${type}${direction}-${size}`;
    }
  };

  // Get positioning classes with RTL support
  const getPositionClass = (direction, size) => {
    // direction: 'left' or 'right'
    // size: the position size
    
    if (direction === 'left' && isRTL) {
      return `right-${size}`;
    } else if (direction === 'right' && isRTL) {
      return `left-${size}`;
    } else {
      return `${direction}-${size}`;
    }
  };

  // Get border radius classes with RTL support
  const getBorderRadiusClass = (direction, size = '') => {
    const sizeClass = size ? `-${size}` : '';
    
    if (direction === 'l' && isRTL) {
      return `rounded-r${sizeClass}`;
    } else if (direction === 'r' && isRTL) {
      return `rounded-l${sizeClass}`;
    } else if (direction === 'tl' && isRTL) {
      return `rounded-tr${sizeClass}`;
    } else if (direction === 'tr' && isRTL) {
      return `rounded-tl${sizeClass}`;
    } else if (direction === 'bl' && isRTL) {
      return `rounded-br${sizeClass}`;
    } else if (direction === 'br' && isRTL) {
      return `rounded-bl${sizeClass}`;
    } else {
      return `rounded-${direction}${sizeClass}`;
    }
  };

  // Get justify content classes with RTL support
  const getJustifyClass = (alignment) => {
    if (alignment === 'start' && isRTL) {
      return 'justify-end';
    } else if (alignment === 'end' && isRTL) {
      return 'justify-start';
    } else {
      return `justify-${alignment}`;
    }
  };

  // Get items alignment classes with RTL support
  const getItemsClass = (alignment) => {
    if (alignment === 'start' && isRTL) {
      return 'items-end';
    } else if (alignment === 'end' && isRTL) {
      return 'items-start';
    } else {
      return `items-${alignment}`;
    }
  };

  // Get icon rotation for RTL
  const getIconClass = (iconType) => {
    const rotations = {
      'chevron-left': isRTL ? 'rotate-180' : '',
      'chevron-right': isRTL ? 'rotate-180' : '',
      'arrow-left': isRTL ? 'rotate-180' : '',
      'arrow-right': isRTL ? 'rotate-180' : '',
    };
    
    return rotations[iconType] || '';
  };

  // Get animation classes with RTL support
  const getAnimationClass = (animationType) => {
    const animations = {
      'slide-in-left': isRTL ? 'slide-in-right' : 'slide-in-left',
      'slide-in-right': isRTL ? 'slide-in-left' : 'slide-in-right',
      'slide-out-left': isRTL ? 'slide-out-right' : 'slide-out-left',
      'slide-out-right': isRTL ? 'slide-out-left' : 'slide-out-right',
    };
    
    return animations[animationType] || animationType;
  };

  // Get space-x classes with RTL support
  const getSpaceXClass = (size) => {
    return isRTL ? `space-x-${size} space-x-reverse` : `space-x-${size}`;
  };

  // Get form input classes with RTL support
  const getFormInputClass = (baseClasses = '') => {
    const rtlClasses = isRTL ? 'text-right dir-rtl' : 'text-left dir-ltr';
    return `${baseClasses} ${rtlClasses}`.trim();
  };

  // Get dropdown position classes
  const getDropdownClass = (baseClasses = '') => {
    const positionClass = isRTL ? 'right-0' : 'left-0';
    return `${baseClasses} ${positionClass}`.trim();
  };

  // Get button icon classes
  const getButtonIconClass = (position = 'left') => {
    if (position === 'left') {
      return isRTL ? 'order-2 mr-2' : 'order-1 ml-2';
    } else {
      return isRTL ? 'order-1 ml-2' : 'order-2 mr-2';
    }
  };

  // Get sidebar classes
  const getSidebarClass = (baseClasses = '') => {
    const positionClass = isRTL ? 'right-0 border-l' : 'left-0 border-r';
    return `${baseClasses} ${positionClass}`.trim();
  };

  // Get table alignment
  const getTableAlignClass = () => {
    return isRTL ? 'text-right' : 'text-left';
  };

  // Get modal classes
  const getModalClass = (baseClasses = '') => {
    const directionClass = isRTL ? 'text-right' : 'text-left';
    return `${baseClasses} ${directionClass}`.trim();
  };

  // Get number/date formatting (always LTR)
  const getNumberClass = () => {
    return 'dir-ltr text-left inline-block';
  };

  const value = {
    currentLanguage: getCurrentLanguage(),
    isRTL,
    changeLanguage,
    getDirectionClass,
    getTextAlign,
    getFlexDirection,
    getSpacingClass,
    getPositionClass,
    getBorderRadiusClass,
    getJustifyClass,
    getItemsClass,
    getIconClass,
    getAnimationClass,
    getSpaceXClass,
    getFormInputClass,
    getDropdownClass,
    getButtonIconClass,
    getSidebarClass,
    getTableAlignClass,
    getModalClass,
    getNumberClass,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}; 